import {
  ActionIcon,
  Button,
  Flex,
  Loader,
  Modal,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconHeartFilled, IconTrash } from "@tabler/icons-react";
import { useEffect } from "react";
import { useGetApi, usePostApi } from "src/hooks";
import { useSelector } from "src/redux/hooks";

import { mapFavourites } from "./FavouritesModal.utils";

interface Props {
  opened: boolean;
  close: () => void;
  onSelect: (favid: string) => void;
}

const FavouritesModal = ({ opened, close, onSelect }: Props) => {
  const lbid = useSelector(state => state.generatorSlice.entryDataToSave.lbid);
  const { post } = usePostApi();

  const {
    data: favourites,
    refetch,
    isLoading,
  } = useGetApi({
    request: "get-favourites",
    body: { params: { lbid } },
    mapper: mapFavourites,
    fetchOnInit: false,
    cache: false,
  });

  useEffect(() => {
    if (opened) void refetch();
  }, [opened, refetch]);

  const handleDelete = (favid: string) => {
    void post({
      request: "delete-favourite",
      body: { params: { favid } },
      onSuccess: () => void refetch(),
    });
  };

  const handleSelect = (favid: string) => {
    close();
    onSelect(favid);
  };

  return (
    <Modal opened={opened} onClose={close} title="New Entry from Favourite">
      {isLoading && !favourites ? (
        <Flex justify="center" py={20}>
          <Loader size="sm" />
        </Flex>
      ) : !favourites?.length ? (
        <Text ta="center" c="dimmed" py={20}>
          No favourites yet. Open an entry and use “Save as Favourite” from the
          menu.
        </Text>
      ) : (
        <Stack gap={8}>
          {favourites.map(fav => (
            <Flex key={fav.favid} align="center" gap={8}>
              <Button
                flex={1}
                variant="light"
                justify="flex-start"
                leftSection={<IconHeartFilled size={16} color="red" />}
                onClick={() => handleSelect(fav.favid)}>
                <Text truncate size="sm">
                  {fav.name}
                  {fav.createdBy && (
                    <Text span c="dimmed" size="xs" ml={6}>
                      {fav.createdBy}
                    </Text>
                  )}
                </Text>
              </Button>
              <Tooltip label="Remove favourite">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  aria-label="Remove favourite"
                  onClick={() => handleDelete(fav.favid)}>
                  <IconTrash size={16} />
                </ActionIcon>
              </Tooltip>
            </Flex>
          ))}
        </Stack>
      )}
    </Modal>
  );
};

export default FavouritesModal;
